import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function TeslaToHawaiiRoutePage() {
  // Dynamically manage SEO browser titles & meta descriptions
  useEffect(() => {
  document.title = "Tesla Shipping Routes USA | State-to-State EV Transport";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Explore popular Tesla shipping routes across the USA, including California to Texas, California to Florida, Hawaii transport options, and nationwide EV shipping services.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'Ship Tesla California to Texas, Ship Tesla from California to Florida, Cross-country Tesla transport USA, Tesla shipping routes USA, Interstate EV transport services, Tesla car shipping routes USA, Tesla transport California to Texas, Tesla transport California to Florida, Coast-to-coast Tesla shipping, Tesla shipping to Hawaii costs, Long-distance EV transport, Door-to-door Tesla shipping USA, State-to-state Tesla transport, Geo-targeted vehicle transport service, Electric vehicle shipping nationwide'
  );
}, []);

  const faqs = [
    {
      question: "Can you ship a Tesla to Hawaii?",
      answer: "Yes -- by combining mainland ground transport to a West Coast port with ocean RoRo or container shipping to the islands. We handle the coordination end to end."
    },
    {
      question: "How long does it take?",
      answer: "Typically 2--4 weeks total, depending on your origin and sailing schedules."
    }
  ];

  // Combined Graph Schema (Service Route + Dynamic FAQPage)
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Tesla Shipping tesla-to-hawaii",
        "areaServed": [
          { "@type": "State", "name": "California" },
          { "@type": "State", "name": "Hawaii" }
        ],
        "serviceType": "Vehicle Transport"
      },
      {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does it take to ship a Tesla to Hawaii?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most shipments take approximately 2 to 4 weeks, including both ground and ocean transport phases."
      }
    },
    {
      "@type": "Question",
      "name": "Why is shipping a Tesla to Hawaii more expensive?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Shipping costs are higher due to ocean freight charges, port handling fees, and additional logistics coordination."
      }
    },
    {
      "@type": "Question",
      "name": "Which ports are used for Tesla shipping to Hawaii?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Typical departure ports include Long Beach, Oakland, and Tacoma on the U.S. West Coast."
      }
    },
    {
      "@type": "Question",
      "name": "Are there special requirements for transporting a Tesla by sea?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, electric vehicles must follow regulated battery charge levels and specialized handling protocols during ocean transit."
      }
    },
    {
      "@type": "Question",
      "name": "Is the shipping process handled end-to-end?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, both ground and ocean transport stages are coordinated under a single service for a seamless experience."
      }
    }
  ]
},
{
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Tesla Shipping to Hawaii",
  "description": "End-to-end Tesla transport service to Hawaii including ground shipping to West Coast ports and ocean freight to the islands, managed by EV-certified carriers with compliant handling protocols.",
  "provider": {
    "@type": "Organization",
    "name": "ShipTesla",
    "url": "https://shiptesla.com"
  },
  "areaServed": [
    {
      "@type": "State",
      "name": "California"
    },
    {
      "@type": "Place",
      "name": "Hawaii"
    }
  ],
  "serviceType": "Electric Vehicle Ocean Transport",
  "availableChannel": {
    "@type": "ServiceChannel",
    "serviceLocation": {
      "@type": "Place",
      "name": "United States"
    },
    "availableLanguage": "English"
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "Tesla Owners, Relocating Individuals, EV Buyers"
  }
}


    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Injecting Structured SEO Data into the DOM */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Oversized &amp; Island Routes</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground" data-testid="route-title">
              Shipping a Tesla to Hawaii: What's Different
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg border-l-2 border-accent pl-4 text-left italic my-6">
              Transporting a Tesla to Hawaii operates as a dual-phase logistics workflow: initial ground transport to a West Coast port—typically Long Beach, Oakland, or Tacoma—followed by ocean freight to the islands. Total transit timelines generally range from 2–4 weeks, with higher overall costs than mainland shipping due to maritime handling and port operations. Our team orchestrates both phases under a unified coordination model, giving you a single, accountable point of contact throughout the process.
            </p>
            <div className="mt-6">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Main Content Body */}
          <div className="space-y-8 text-muted-foreground md:text-lg leading-relaxed mb-12">
            <p>
              Given the technical requirements associated with electric vehicles, including regulated battery charge levels and specialized handling protocols during ocean transit, it is critical to work with an EV-experienced carrier network. This ensures compliance, safety, and consistent delivery performance across both land and sea segments.
            </p>
          </div>

          {/* Route-Specific FAQs */}
          <div className="mt-12 border-t border-border pt-12">
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-5 text-foreground">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA Block */}
          <div className="mt-16 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Guides Links Layout */}
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="font-outfit text-lg font-semibold text-foreground mb-4">Related guides</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ship-a-tesla" className="text-accent hover:underline">How to Ship a Tesla</Link></li>
              <li><Link to="/tesla-shipping-cost" className="text-accent hover:underline">Tesla Shipping Cost: 2026 Price Guide</Link></li>
            </ul>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}


